// Libraries
import { CommonModule } from '@angular/common';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Component, inject, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule, ProgressBarMode } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { add, isBefore, sub } from 'date-fns';
import { map, Subject, takeUntil } from 'rxjs';

// Services
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { EventService } from 'src/app/services/api/events/event.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';

// Classes
import { Pop } from 'src/app/classes/shared/pop';

// UI
import { ButtonComponent, RangePickerComponent } from 'ui-library';

// Environment
import { Tools } from 'src/app/classes/shared/tools';
import { environment } from 'src/environments/environment';


@Component({
    selector: 'app-database',
    imports: [
        CommonModule,
        FormsModule,
        ButtonComponent,
        RangePickerComponent,
        TranslateModule,
        MatToolbarModule,
        MatFormFieldModule,
        MatSelectModule,
        MatProgressBarModule,
        MatIconModule
    ],
    standalone: true,
    templateUrl: './database.component.html',
    styleUrl: './database.component.scss'
})

export class DatabaseComponent implements OnDestroy {
    // Services
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly http: HttpClient = inject(HttpClient);
    private readonly trackService: TrackService = inject(TrackService);
    private readonly eventService: EventService = inject(EventService);
    private readonly metricService: MetricService = inject(MetricService);
    private readonly attachmentService: AttachmentService = inject(AttachmentService);
    private readonly pop: Pop = inject(Pop);
    private readonly tools: Tools = inject(Tools);

    // Variables
    currentCollection: string = "tracks";
    exportFormat: string = "json";
    startDate: string = new Date(sub(new Date(), { days: 7 }).setHours(0, 0, 0, 0)).toISOString();
    endDate: string = new Date().toISOString();
    progressColor: ThemePalette = 'primary';
    progressMode: ProgressBarMode = 'buffer';
    progressValue: number = 0;
    progressBufferValue: number = 0;
    progressText: string = "";
    socketId: string = (Math.random() * 100 * 1000).toFixed(0).toString();
    destroy$ = new Subject<void>();

    constructor() { }

    /////////////////////////////
    // Angular lifecycle hooks //
    /////////////////////////////

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    downloadURL(path: string) {
        try {
            let pathArray: string[] = path.split('/');
            let extArray = path.split('.');
            let extension = extArray[extArray.length - 1];

            if (extension == "csv") {
                const link = document.createElement('a');

                if (link.download !== undefined) {
                    link.setAttribute('href', environment.API_URL + path);
                    link.setAttribute('download', environment.API_URL + path);
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                this.progressText = `Export réussi du ${this.tools.formatDateFR(this.startDate)} au ${this.tools.formatDateFR(this.endDate)}`;
                this.progressValue = 100;
                this.progressBufferValue = 100;
            } else {
                this.progressBufferValue = 100;

                this.http.get(environment.API_URL + path, {
                    reportProgress: true,
                    observe: 'events',
                }).pipe(
                    map((event: any) => {
                        switch (event.type) {
                            case HttpEventType.DownloadProgress:
                                if (event.total) {
                                    const progress = Math.round(100 * event.loaded / event.total);
                                    return { progress };
                                } else {
                                    return { loaded: event.loaded };
                                }
                            case HttpEventType.Response:
                                return { data: event.body };
                            default:
                                return `Unhandled event: ${event.type}`;
                        }
                    })
                ).pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
                    if (data.progress) {
                        this.progressText = this.translate.instant('status.downloading') + ': ' + data.progress + '%';
                        this.progressValue = (data.progress / 2) + 50;
                    } else if (data.data) {
                        this.download(data.data, pathArray[pathArray.length - 1])
                        this.progressText = `Export réussi du ${this.tools.formatDateFR(this.startDate)} au ${this.tools.formatDateFR(this.endDate)}`;
                        this.progressValue = 100;
                        this.progressBufferValue = 100;
                    }
                });
            }
        } catch (e: any) {
            console.error(e);
            this.progressText = e.toString();
        }
    }

    download(content: any, filename: string = 'download.json') {
        try {
            let text = JSON.stringify(content);
            let mime = "application/json";
            const blob = new Blob([text], { type: mime + ';charset=utf-8;' });
            const link = document.createElement('a');

            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                this.progressText = "";
            }
        } catch (e: any) {
            console.error(e);
            this.progressText = e.toString();
        }
    }

    exportData() {
        this.progressText = this.translate.instant('status.export_db');
        this.progressBufferValue = 50;

        if (this.currentCollection == "tracks") {
            this.trackService.exportTracks({ startDate: this.startDate, endDate: this.endDate, maxData: 1000000, format: this.exportFormat, socketId: this.socketId }).pipe(takeUntil(this.destroy$)).subscribe({
                next: (data: any) => {
                    if (data.error) {
                        this.progressBufferValue = 0;
                        this.progressText = "ERROR: " + data.error;
                    }

                    if (data.path) {
                        this.downloadURL(data.path);
                    }
                },
                error: (err: any) => {
                    this.progressText = "";
                    this.progressValue = 0;
                    this.progressBufferValue = 0;
                    this.pop.error(err.error.error || err.error.message);
                }
            })
        } else if (this.currentCollection == "attachments") {
            this.attachmentService.exportAttachments({ startDate: this.startDate, endDate: this.endDate, maxData: 1000000, format: this.exportFormat, socketId: this.socketId }).pipe(takeUntil(this.destroy$)).subscribe({
                next: (data: any) => {
                    if (data.error) {
                        this.progressBufferValue = 0;
                        this.progressText = "ERROR: " + data.error;
                    }

                    if (data.path) {
                        this.downloadURL(data.path);
                    }
                },
                error: (err: any) => {
                    this.progressText = "";
                    this.progressValue = 0;
                    this.progressBufferValue = 0;
                    this.pop.error(err.error.error || err.error.message);
                }
            })

        } if (this.currentCollection == "events") {
            this.eventService.exportEvents({ startDate: this.startDate, endDate: this.endDate, maxData: 1000000, format: this.exportFormat, socketId: this.socketId }).pipe(takeUntil(this.destroy$)).subscribe({
                next: (data: any) => {
                    if (data.error) {
                        this.progressBufferValue = 0;
                        this.progressText = "ERROR: " + data.error;
                    }

                    if (data.path) {
                        this.downloadURL(data.path);
                    }
                },
                error: (err: any) => {
                    this.progressText = "";
                    this.progressValue = 0;
                    this.progressBufferValue = 0;
                    this.pop.error(err.error.error || err.error.message);
                }
            })

        } else if (this.currentCollection == "metrics") {
            this.metricService.exportMetrics({ startDate: this.startDate, endDate: this.endDate, maxData: 1000000, format: this.exportFormat, socketId: this.socketId }).pipe(takeUntil(this.destroy$)).subscribe({
                next: (data: any) => {
                    if (data.error) {
                        this.progressBufferValue = 0;
                        this.progressText = "ERROR: " + data.error;
                    }

                    if (data.path) {
                        this.downloadURL(data.path);
                    }
                },
                error: (err: any) => {
                    this.progressText = "";
                    this.progressValue = 0;
                    this.progressBufferValue = 0;
                    this.pop.error(err.error.error || err.error.message);
                }
            })
        }
    }

    onPickerChanged(result: any) {
        if (this.startDate !== result.startDate) {
            this.startDate = result.startDate;

            if (isBefore(this.endDate, result.startDate)) {
                this.endDate = add(result.startDate, { hours: 1 }).toISOString();
            }
        }

        if (this.endDate !== result.endDate) {
            this.endDate = result.endDate;

            if (isBefore(result.endDate, this.startDate)) {
                this.startDate = sub(result.endDate, { hours: 1 }).toISOString();
            }
        }
    }
}
